import WebsiteSettings from "../pages/Settings/Website";
import { lazy, Suspense, type ComponentType } from "react";
import { createBrowserRouter, Navigate, useParams } from "react-router-dom";

// Core layout components (keep eager - small and always needed)
import Faqs from "../components/dashboard/faqs";
import PageLoader from "../components/PageLoader";
import WeddnLoader from "../components/WeddnLoader";
import Auth from "../Layout/Auth";
import DashboardLayout from "../Layout/Dashboard";
import TailwindLayout from "../Layout/TailwindLayout";
import MessagesPage from "../pages/Dashboard/messages";
import Notifications from "../pages/Dashboard/notifications";
import SendAMessage from "../pages/Dashboard/send-a-message";
import WeddnProSubscription from "../pages/Dashboard/subscription";
import Support from "../pages/Dashboard/support";
import MyWeddn from "../pages/Dashboard/MyWeddn";
import ErrorPage from "../pages/ErrorPage/Error";
import AccessCardPage from "../pages/AccessCard";
import AuthRouteProvider from "./AuthRoutesProvider";
import { Routes } from "./baseRoutes";
import EventPlannerRouteProvider from "./EventPlannerRoutesProvider";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import PlannerWorkspaceLayout from "../Layout/Dashboard/eventPlaner/PlannerWorkspaceLayout";

// Lazy load all page components for code splitting
const SignIn = lazy(() => import("../pages/Auth/SignIn"));
const RegisterForm = lazy(() => import("../pages/Auth"));
const RegisterSpouse = lazy(() => import("../pages/Auth/RegisterSpouse"));
const PlannerInvite = lazy(() => import("../pages/Auth/PlannerInvite"));
const ResetPassword = lazy(() =>
  import("../pages/Auth/ResetPassword").then((module) => ({
    default: module.ResetPassword,
  }))
);

// Dashboard
const Overview = lazy(() => import("../pages/Dashboard"));
const CoupleWeddingPage = lazy(
  () => import("../pages/Dashboard/CoupleWeddnPage")
);
const WeddnList = lazy(
  () => import("../pages/Dashboard/EventPlanner/WeddnList")
);
const EventPlannerDashboard = lazy(() => import("../pages/Dashboard/EventPlanner"));
const PlannerInvitations = lazy(() => import("../pages/Dashboard/EventPlanner/PlannerInvitations"));
const PlannerOperations = lazy(() => import("../pages/Dashboard/EventPlanner/PlannerOperations"));
const PlannerTeamInvite = lazy(() => import("../pages/Dashboard/EventPlanner/PlannerTeamInvite"));
const DesignStudio = lazy(() => import("../pages/Designs"));
const AsoEbiManager = lazy(() => import("../pages/AsoEbi"));
const LoveWallManager = lazy(() => import("../pages/LoveWall"));

const DesignStudioRoute = () => {
  const { id: plannerWeddingID } = useParams<{ id: string }>();
  const { enabled, loading } = useFeatureFlag("design_studio");
  if (loading) return <PageLoader />;
  // Never eject the event planner out of the wedding workspace: `/` is the
  // couple dashboard and immediately redirects event planners to `/planner`.
  // Fall back to this wedding's own design configuration instead.
  if (!enabled) {
    return (
      <Navigate
        to={
          plannerWeddingID
            ? `/planner/weddings/${plannerWeddingID}/website/design`
            : "/"
        }
        replace
      />
    );
  }
  return withSuspense(DesignStudio);
};

// Guests
const Guests = lazy(() => import("../pages/Guests"));
const ManageGuests = lazy(() => import("../pages/Guests/ManageGuests"));
const DeletedGuests = lazy(() => import("../pages/Guests/DeletedGuests"));
const TablePlan = lazy(() => import("../pages/Guests/TablePlan"));
const WellWishes = lazy(() => import("../pages/Guests/WellWishes"));
const Messaging = lazy(() => import("../pages/Guests/Messaging"));
const MessagingPage = lazy(() => import("../pages/Guests/Messaging/messaging"));
const MessagingDetails = lazy(
  () => import("../pages/Guests/Messaging/messaging-details")
);
const MessagingTemplates = lazy(
  () => import("../pages/Guests/Messaging/templates")
);
const CreateMessage = lazy(
  () => import("../pages/Guests/Messaging/create-message")
);
const SendMessagePage = lazy(
  () => import("../pages/Guests/Messaging/send-message")
);
const Preferences = lazy(() => import("../pages/Guests/Preferences"));

// Website
const Website = lazy(() => import("../pages/Website"));
const WebsiteDesign = lazy(
  () => import("../pages/Website/pages/design.website")
);
const EditWebiteLayout = lazy(() =>
  import("../pages/Website/pages/EditWebsite").then((module) => ({
    default: module.EditWebiteLayout,
  }))
);
const EditWebsite = lazy(() =>
  import("../pages/Website/pages/EditWebsite").then((module) => ({
    default: module.EditWebsite,
  }))
);
const Basics = lazy(() =>
  import("../pages/Website/pages/EditWebsite").then((module) => ({
    default: module.Basics,
  }))
);
const LoveStory = lazy(() =>
  import("../pages/Website/pages/EditWebsite").then((module) => ({
    default: module.LoveStory,
  }))
);
const AsoEbi = lazy(() =>
  import("../pages/Website/pages/EditWebsite").then((module) => ({
    default: module.AsoEbi,
  }))
);
const EventSchedule = lazy(() =>
  import("../pages/Website/pages/EditWebsite").then((module) => ({
    default: module.EventSchedule,
  }))
);
const Gallery = lazy(() =>
  import("../pages/Website/pages/EditWebsite").then((module) => ({
    default: module.Gallery,
  }))
);
const Gifting = lazy(() =>
  import("../pages/Website/pages/EditWebsite").then((module) => ({
    default: module.Gifting,
  }))
);
const QAndA = lazy(() =>
  import("../pages/Website/pages/EditWebsite").then((module) => ({
    default: module.QAndA,
  }))
);
const WeddingParty = lazy(() =>
  import("../pages/Website/pages/EditWebsite").then((module) => ({
    default: module.WeddingParty,
  }))
);

// Settings
const Settings = lazy(() => import("../pages/Settings"));
const Account = lazy(() => import("../pages/Settings/Account"));
const Collaboration = lazy(() => import("../pages/Settings/Collaboration"));
const ShareFeedback = lazy(() => import("../pages/Settings/ShareFeedback"));
const Subscription = lazy(() => import("../pages/Settings/Subscription"));

// Catering
const CateringCmp = lazy(() => import("../pages/Catering"));
const Catering = lazy(() => import("../pages/Catering/Catering"));
const CateringCentre = lazy(() => import("../pages/Catering/CateringCentre"));
// Suspense wrapper for lazy components
const withSuspense = (Component: ComponentType) => (
  <Suspense fallback={<WeddnLoader variant="fullscreen" />}>
    <Component />
  </Suspense>
);

const LegacyEventPlannerDesignRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/planner/weddings/${id}/design`} replace />;
};

const LegacyPlannerRedirect = () => {
  const { id, "*": rest = "" } = useParams();
  return <Navigate to={`/planner/weddings/${id}${rest ? `/${rest}` : ""}`} replace />;
};

const router = createBrowserRouter([
  {
    path: Routes.login,
    element: <Auth>{withSuspense(SignIn)}</Auth>,
    errorElement: <ErrorPage />,
  },
  {
    path: Routes.register,
    element: (
      <Auth>
        <RegisterForm />
      </Auth>
    ),
    errorElement: <ErrorPage />,
  },
  {
	path: Routes.plannerInvite,
	element: <Auth>{withSuspense(PlannerInvite)}</Auth>,
	errorElement: <ErrorPage />,
  },
  {
    path: Routes.registerSpouse,
    element: (
      <Auth>
        <RegisterSpouse />
      </Auth>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: Routes.resetPassword,
    element: (
      <Auth>
        <ResetPassword />
      </Auth>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: Routes.index,
    element: (
      <AuthRouteProvider>
        <DashboardLayout />
      </AuthRouteProvider>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: withSuspense(Overview),
      },
      {
        path: Routes.myWeddn,
        element: <MyWeddn />,
      },
      {
        path: Routes.planner,
        element: <PlannerWorkspaceLayout />,
        children: [
          { index: true, element: withSuspense(EventPlannerDashboard) },
          { path: "weddings", element: withSuspense(WeddnList) },
          { path: "invitations", element: withSuspense(PlannerInvitations) },
          { path: "team-invite", element: withSuspense(PlannerTeamInvite) },
          { path: "calendar", element: withSuspense(PlannerOperations) },
          { path: "team", element: withSuspense(PlannerOperations) },
          { path: "tasks", element: withSuspense(PlannerOperations) },
          { path: "templates", element: withSuspense(PlannerOperations) },
          { path: "activity", element: withSuspense(PlannerOperations) },
        ],
      },
      {
        path: Routes.eventPlannerWedding,
        element: <EventPlannerRouteProvider />,
        children: [
          {
            index: true,
            element: <CoupleWeddingPage />,
          },
          {
            path: Routes.eventPlannerCatering,
            element: <CateringCmp />,
            children: [
              {
                index: true,
                element: <Catering />,
              },
              {
                path: Routes.eventPlannerCateringCentre,
                element: <CateringCentre />,
              },
            ],
          },
          {
            path: Routes.eventPlannerGuests,
            element: <Guests />,
            children: [
              {
                path: Routes.eventPlannerGuestsAccessCards,
                element: <AccessCardPage />,
              },
              {
                path: Routes.eventPlannerGuestsWellWishes,
                element: <WellWishes />,
              },
              {
                path: Routes.eventPlannerGuestsTablePlan,
                element: <TablePlan />,
              },
              {
                path: Routes.eventPlannerGuestsMessaging,
                element: <Messaging />,
              },
              {
                path: Routes.eventPlannerGuestsPreference,
                element: <Preferences />,
              },
              {
                path: Routes.eventPlannerGuestsDeleted,
                element: <DeletedGuests />,
              },
              {
                path: Routes.eventPlannerGuests,
                element: <ManageGuests />,
              },
            ],
          },
          {
            path: Routes.eventPlannerWebsite,
            element: withSuspense(Website),
            children: [
              {
                path: Routes.eventPlannerWebsiteDesign,
                element: <WebsiteDesign />,
              },
              {
                path: Routes.eventPlannerEditWebsite,
                element: <EditWebiteLayout />,
                children: [
                  {
                    path: Routes.eventPlannerBasicsEdit,
                    element: <Basics />,
                  },
                  {
                    path: Routes.eventPlannerLoveStoryEdit,
                    element: <LoveStory />,
                  },
                  {
                    path: Routes.eventPlannerAsoEbiEdit,
                    element: <AsoEbi />,
                  },
                  {
                    path: Routes.eventPlannerEventSchedule,
                    element: <EventSchedule />,
                  },
                  {
                    path: Routes.eventPlannerGalleryEdit,
                    element: <Gallery />,
                  },
                  {
                    path: Routes.eventPlannerGiftingEdit,
                    element: <Gifting />,
                  },
                  {
                    path: Routes.eventPlannerQAndAEdit,
                    element: <QAndA />,
                  },
                  {
                    path: Routes.eventPlannerWeddingPartyEdit,
                    element: <WeddingParty />,
                  },
                  {
                    index: true,
                    element: <EditWebsite />,
                  },
                ],
              },
            ],
          },
          {
            path: Routes.eventPlannerWeddnProSubscription,
            element: <WeddnProSubscription />,
          },
          {
            path: Routes.eventPlannerDesigns,
            element: <DesignStudioRoute />,
          },
          {
            path: Routes.eventPlannerDesignEditor,
            element: <DesignStudioRoute />,
          },
          {
            path: Routes.eventPlannerAsoEbi,
            element: withSuspense(AsoEbiManager),
          },
          {
            path: Routes.eventPlannerLoveWall,
            element: withSuspense(LoveWallManager),
          },
          {
            path: "/planner/weddings/:id/designs",
            element: <LegacyEventPlannerDesignRedirect />,
          },
          {
            path: Routes.eventSupport,
            element: <Support />,
          },
          {
            path: Routes.eventPlannerSettings,
            element: <Settings />,
            children: [
              {
                path: Routes.eventPlannerSettings,
                element: <Account />,
              },
              { path: Routes.eventPlannerWebsiteSettings, element: <WebsiteSettings /> },
              {
                path: Routes.eventPlannerCollaborationsSettings,
                element: <Collaboration />,
              },
              {
                path: Routes.eventPlannerShareFeedbackSettings,
                element: <ShareFeedback />,
              },
              {
                path: Routes.eventPlannerSubscriptionSettings,
                element: <Subscription />,
              },
            ],
          },
        ],
      },
      {
        path: "/wdn/:id/*",
        element: <LegacyPlannerRedirect />,
      },
      {
        path: Routes.guests,
        element: <Guests />,
        children: [
          {
            path: Routes.guestsAccessCards,
            element: <Navigate to={Routes.accessCard} replace />,
          },
          {
            path: Routes.guestsWellWishes,
            element: <WellWishes />,
          },
          {
            path: Routes.guestsTablePlan,
            element: <TablePlan />,
          },
          {
            path: Routes.guestsMessaging,
            element: <Messaging />,
          },
          {
            path: Routes.guestsPreference,
            element: <Preferences />,
          },
          {
            path: Routes.guestsDeleted,
            element: <DeletedGuests />,
          },
          {
            path: Routes.guests,
            element: <ManageGuests />,
          },
        ],
      },
      {
        path: Routes.catering,
        element: <CateringCmp />,
        children: [
          {
            index: true,
            element: <Catering />,
          },
          {
            path: Routes.cateringCentre,
            element: <CateringCentre />,
          },
        ],
      },
      {
        path: Routes.designs,
        element: <DesignStudioRoute />,
      },
      {
        path: Routes.designEditor,
        element: <DesignStudioRoute />,
      },
      {
        path: Routes.asoEbi,
        element: withSuspense(AsoEbiManager),
      },
      {
        path: Routes.loveWall,
        element: withSuspense(LoveWallManager),
      },
      {
        path: "/designs",
        element: <Navigate to={Routes.designs} replace />,
      },
      {
        path: Routes.website,
        element: withSuspense(Website),
        children: [
          {
            path: Routes.websiteDesign,
            element: <WebsiteDesign />,
          },
          {
            path: Routes.editWebsite,
            element: <EditWebiteLayout />,
            children: [
              {
                path: Routes.basicsEdit,
                element: <Basics />,
              },
              {
                path: Routes.loveStoryEdit,
                element: <LoveStory />,
              },
              {
                path: Routes.asoEbiEdit,
                element: <AsoEbi />,
              },
              {
                path: Routes.eventSchedule,
                element: <EventSchedule />,
              },
              {
                path: Routes.galleryEdit,
                element: <Gallery />,
              },
              {
                path: Routes.giftingEdit,
                element: <Gifting />,
              },
              {
                path: Routes.qAndAEdit,
                element: <QAndA />,
              },
              {
                path: Routes.weddingPartyEdit,
                element: <WeddingParty />,
              },
              {
                index: true,
                element: <EditWebsite />,
              },
            ],
          },
        ],
      },
      {
        path: Routes.settings,
        element: <Settings />,
        children: [
          {
            path: Routes.settings,
            element: <Account />,
          },
          { path: Routes.WebsiteSettings, element: <WebsiteSettings /> },
              {
            path: Routes.CollaborationsSettings,
            element: <Collaboration />,
          },
          {
            path: Routes.ShareFeedbackSettings,
            element: <ShareFeedback />,
          },
          {
            path: Routes.SubscriptionSettings,
            element: <Subscription />,
          },
        ],
      },
      {
        path: Routes.messaging,
        element: withSuspense(MessagingPage),
      },
      {
        path: Routes.messagingTemplates,
        element: withSuspense(MessagingTemplates),
      },
      {
        path: Routes.messagingCreate,
        element: withSuspense(CreateMessage),
      },
      {
        path: Routes.messagingSend,
        element: withSuspense(SendMessagePage),
      },
      {
        path: Routes.messagingDetails,
        element: withSuspense(MessagingDetails),
      },
      {
        path: Routes.notifications,
        element: <Notifications />,
      },
      {
        path: Routes.weddnProSubscription,
        element: <WeddnProSubscription />,
      },
      {
        path: Routes.support,
        element: <Support />,
      },
      {
        path: Routes.sendMessage,
        element: <SendAMessage />,
      },
      {
        path: Routes.messages,
        element: <MessagesPage />,
      },
      {
        path: Routes.faqs,
        element: <Faqs />,
      },
    ],
  },
  {
    path: Routes.accessCard,
    element: (
      <AuthRouteProvider>
        <TailwindLayout />
      </AuthRouteProvider>
    ),
    children: [
      {
        index: true,
        element: <AccessCardPage />,
      },
    ],
    errorElement: <ErrorPage />,
  },
  {
    path: Routes.legacyAccessCard,
    element: <Navigate to={Routes.accessCard} replace />,
  },
  {
    path: Routes.notFound,
    element: <ErrorPage />,
  },
]);

export default router;
